/**
 * @graphql experimental
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization';

import type {
	useModernizeChangeboardingTapTraitsQuery as useModernizeChangeboardingTapTraitsQuery$data,
	useModernizeChangeboardingTapTraitsQueryVariables as useModernizeChangeboardingTapTraitsQuery$variables,
} from './__types__/useModernizeChangeboardingTapTraitsQuery';

export const useModernizeChangeboardingTapTraits = () => {
	const { cloudId, userId } = useSessionData();

	const queryArgs = {
		variables: { cloudId, userId: userId ?? '' },
		skip: !userId,
	};

	const { loading, data, error } = expValEquals(
		'confluence_deprecate_experimental_personalization',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalSiteUserPersonalization(queryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<
				useModernizeChangeboardingTapTraitsQuery$data,
				useModernizeChangeboardingTapTraitsQuery$variables
			>(
				// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
				gql`
					query useModernizeChangeboardingTapTraitsQuery($cloudId: String!, $userId: String!)
					@experimental {
						experimentalSiteUserPersonalization(cloudId: $cloudId, userId: $userId) {
							attributes {
								name
								value
							}
						}
					}
				`,
				queryArgs,
			);

	if (!data) {
		return {
			isInNav4Audience: false,
			hasDismissedNav4ChangeboardingViaEP: false,
			loading,
			error,
		};
	}

	let isInNav4Audience = false;
	let hasDismissedNav4ChangeboardingViaEP = false;
	for (const { name, value } of data.experimentalSiteUserPersonalization?.attributes ?? []) {
		if (name === 'segment_confluence_nav_4_changeboarding_cloned_siteuser') {
			isInNav4Audience = value === 'true';
		} else if (name === 'completed_cc-nav-4-changeboarding-prod') {
			hasDismissedNav4ChangeboardingViaEP = Boolean(value);
		}
	}

	return {
		isInNav4Audience,
		hasDismissedNav4ChangeboardingViaEP,
		loading,
		error,
	};
};
